import React from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { css } from '@compiled/react';

import UploadIcon from '@atlaskit/icon/core/upload';
import { token } from '@atlaskit/tokens';
import Button from '@atlaskit/button/button';

import { useTwcg2304ImportInOnboarding } from '../hooks/useTwcg2304ImportInOnboarding.ts';

const i18n = defineMessages({
	importLabel: {
		id: 'experiment-twcg-2304-import-in-onboarding.import-cta.label',
		defaultMessage: 'Import',
		description: 'Label for the Import CTA button shown in the left nav during onboarding',
	},
});

const iconWrapperStyles = css({
	color: token('color.icon.subtle'),
});

const titleWrapperStyles = css({
	display: 'flex',
	color: token('color.text.subtle'),
	fontWeight: token('font.weight.medium'),
	marginLeft: token('space.050'),
});

const buttonWrapperStyles = css({
	height: '36px',
	marginLeft: token('space.0'),
	alignItems: 'center',
	display: 'flex',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- override button internal padding
	button: {
		padding: '0 8px',
	},
});

/**
 * "Import" CTA rendered in the space side navigation for new users.
 *
 * This component is 100% gated behind the twcg-2304-import-in-onboarding experiment.
 * It renders nothing when:
 * - The experiment is loading
 * - The experiment is off (control arm)
 * - The user is not eligible (not founding admin, or outside first 2 weeks)
 *
 * Styled to match the TreeCreateContentButton ("+ Create" button) in the page tree.
 */
export const ImportOnboardingCTA = ({
	onClick,
}: {
	onClick: () => void;
}): React.JSX.Element | null => {
	const { isEnabled, loading } = useTwcg2304ImportInOnboarding();
	const intl = useIntl();

	if (loading || !isEnabled) {
		return null;
	}

	const icon = (
		<span css={iconWrapperStyles}>
			<UploadIcon label="" size="small" />
		</span>
	);

	return (
		<div css={buttonWrapperStyles}>
			<Button
				onClick={onClick}
				testId="import-onboarding-cta"
				iconBefore={icon}
				appearance="subtle"
				shouldFitContainer
			>
				<span css={titleWrapperStyles}>{intl.formatMessage(i18n.importLabel)}</span>
			</Button>
		</div>
	);
};
